/**
 * UI Labels Index
 * Centralized export of all default UI labels
 */
import { COMMON_LABELS, CommonLabelKey } from "./common";
import { TALENT_LABELS, TalentLabelKey } from "./talent";
import { PROJECT_LABELS, ProjectLabelKey } from "./project";
import { CLIENT_LABELS, ClientLabelKey } from "./client";
import { MANAGER_LABELS, ManagerLabelKey } from "./manager";
import { LOCATION_LABELS, LocationLabelKey } from "./location";

// Combined labels object for easy lookup
export const DEFAULT_LABELS = {
  common: COMMON_LABELS,
  talent: TALENT_LABELS,
  project: PROJECT_LABELS,
  client: CLIENT_LABELS,
  manager: MANAGER_LABELS,
  location: LOCATION_LABELS,
} as const;

// Type for label keys (e.g., "common.location", "project.location")
export type UILabelKey = 
  | `common.${CommonLabelKey}`
  | `talent.${TalentLabelKey}`
  | `project.${ProjectLabelKey}`
  | `client.${ClientLabelKey}`
  | `manager.${ManagerLabelKey}`
  | `location.${LocationLabelKey}`;

/**
 * Get default label by key
 * @param key - Label key in format "module.key" (e.g., "common.location")
 * @returns Default label value or the key itself if not found
 */
export function getDefaultLabel(key: string): string {
  const [module, labelKey] = key.split(".");
  
  if (!module || !labelKey) {
    return key; // Return key if format is invalid
  }

  const moduleLabels = DEFAULT_LABELS[module as keyof typeof DEFAULT_LABELS];
  if (!moduleLabels) {
    return key; // Return key if module not found
  }

  const label = moduleLabels[labelKey as keyof typeof moduleLabels];
  return label || key; // Return label or key if not found
}

/**
 * Get all available label keys
 * @returns Array of all available label keys
 */
export function getAllLabelKeys(): UILabelKey[] {
  const keys: UILabelKey[] = [];
  
  Object.keys(COMMON_LABELS).forEach((key) => {
    keys.push(`common.${key}` as UILabelKey);
  });
  
  Object.keys(TALENT_LABELS).forEach((key) => {
    keys.push(`talent.${key}` as UILabelKey);
  });
  
  Object.keys(PROJECT_LABELS).forEach((key) => {
    keys.push(`project.${key}` as UILabelKey);
  });
  
  Object.keys(CLIENT_LABELS).forEach((key) => {
    keys.push(`client.${key}` as UILabelKey);
  });
  
  Object.keys(MANAGER_LABELS).forEach((key) => {
    keys.push(`manager.${key}` as UILabelKey);
  });
  
  Object.keys(LOCATION_LABELS).forEach((key) => {
    keys.push(`location.${key}` as UILabelKey);
  });
  
  return keys;
}

// Export individual label objects for direct access if needed
export { 
  COMMON_LABELS, 
  TALENT_LABELS, 
  PROJECT_LABELS,
  CLIENT_LABELS,
  MANAGER_LABELS,
  LOCATION_LABELS,
};
export type { 
  CommonLabelKey, 
  TalentLabelKey, 
  ProjectLabelKey,
  ClientLabelKey,
  ManagerLabelKey,
  LocationLabelKey,
};

